import { useAppKitState } from "@reown/appkit/react"
import { useEffect, useState, type ReactNode } from "react"
import { ImgLogo } from "@/assets/images"
import { cn } from "@/lib/utils"
import { useStaticConfig } from "@/hooks/use-static-config"
import { useDelayLoading } from "@/hooks/use-delay-loading"

const MIN_DISPLAY_MS = 1500
const REVEAL_MS = 520

interface AppLoadingProps {
  children: ReactNode
}

export function AppLoadingScreen({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-8 bg-background",
        className
      )}
    >
      <div className="flex flex-col items-center gap-6">
        <img
          src={ImgLogo}
          alt=""
          className="h-10 w-auto opacity-90"
          aria-hidden
        />
        <div className="h-0.5 w-28 overflow-hidden rounded-full bg-border">
          <div className="h-full w-10 animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-primary" />
        </div>
      </div>
    </div>
  )
}

/**
 * Full-app loading gate. Shows loading UI until critical deps (e.g. Appkit, Static Config) are ready
 * and page has mounted. Always displays at least MIN_DISPLAY_MS to avoid jarring flash.
 */
export function AppLoading({ children }: AppLoadingProps) {
  const { initialized } = useAppKitState()
  const { isPending: isConfigPending } = useStaticConfig()
  const [minElapsed, setMinElapsed] = useState(false)
  const [isAppReady, setIsAppReady] = useState(false)
  const [showOverlay, setShowOverlay] = useState(true)

  useEffect(() => {
    const t = setTimeout(() => {
      setMinElapsed(true)
    }, MIN_DISPLAY_MS)
    return () => {
      clearTimeout(t)
    }
  }, [])

  const ready = initialized && minElapsed && !isConfigPending

  // Permanently lock ready state once resolved to prevent any web3/query flicker from re-triggering overlay.
  useEffect(() => {
    if (ready) {
      setIsAppReady(true)
    }
  }, [ready])

  // Hold the loading overlay for a brief 500ms after ready is resolved to give Web3 context and initial redirects time to settle.
  const delayedIsLoading = useDelayLoading(!isAppReady, 500)
  const delayedReady = !delayedIsLoading

  useEffect(() => {
    if (!delayedReady) {
      setShowOverlay(true)
      return
    }

    const t = setTimeout(() => {
      setShowOverlay(false)
    }, REVEAL_MS)
    return () => clearTimeout(t)
  }, [delayedReady])

  return (
    <div className="relative h-full w-full overflow-hidden bg-background">
      <div
        className={cn(
          "h-full w-full",
          ready
            ? "visible animate-in fade-in-0 slide-in-from-bottom-2 duration-500 ease-out will-change-[opacity,transform]"
            : "invisible pointer-events-none"
        )}
      >
        {children}
      </div>
      {showOverlay && (
        <AppLoadingScreen
          className={cn(
            "fixed inset-0 z-50 transition-opacity duration-500 ease-out",
            delayedReady ? "opacity-0 pointer-events-none" : "opacity-100"
          )}
        />
      )}
    </div>
  )
}
